import { Badge } from "@bcc/components/ui/badge";
import { Card, CardContent } from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Package, Search, ShoppingCart, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { setSearchViewTestId } from "./search-view.utils";

interface SearchResult {
  type: "product" | "order" | "customer";
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  href: string;
}

export interface SearchViewProps {
  testId?: string;
}

export function SearchView({ testId }: SearchViewProps = {}) {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const query = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(query);
  const [results, setResults] = useState<SearchResult[]>([]);

  useEffect(() => {
    setSearchQuery(query);
    if (query.trim()) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = (q: string) => {
    const lowerQuery = q.toLowerCase();
    const mockResults: SearchResult[] = [];

    // Mock product search
    const products = [
      {
        id: "1",
        name: "Ethiopia Yirgacheffe",
        roaster: "Artisan Roasters",
        status: "Active",
      },
      {
        id: "2",
        name: "Colombia Supremo",
        roaster: "Peak Coffee",
        status: "Active",
      },
      {
        id: "3",
        name: "Brazil Santos",
        roaster: "Mountain Brew",
        status: "Draft",
      },
    ];
    products.forEach((p) => {
      if (
        p.name.toLowerCase().includes(lowerQuery) ||
        p.roaster.toLowerCase().includes(lowerQuery)
      ) {
        mockResults.push({
          type: "product",
          id: p.id,
          title: p.name,
          subtitle: p.roaster,
          badge: p.status,
          href: `/products?id=${p.id}`,
        });
      }
    });

    // Mock order search
    const orders = [
      { id: "ORD-001", customer: "Cafe Milano", status: "Pending" },
      { id: "ORD-002", customer: "Bean Street", status: "Shipped" },
      { id: "ORD-003", customer: "Coffee Corner", status: "Delivered" },
    ];
    orders.forEach((o) => {
      if (
        o.id.toLowerCase().includes(lowerQuery) ||
        o.customer.toLowerCase().includes(lowerQuery)
      ) {
        mockResults.push({
          type: "order",
          id: o.id,
          title: o.id,
          subtitle: o.customer,
          badge: o.status,
          href: `/orders?id=${o.id}`,
        });
      }
    });

    // Mock customer search
    const customers = [
      { id: "1", name: "Cafe Milano", email: "info@cafemilano.com" },
      { id: "2", name: "Bean Street", email: "hello@beanstreet.com" },
      { id: "3", name: "Coffee Corner", email: "contact@coffeecorner.com" },
    ];
    customers.forEach((c) => {
      if (
        c.name.toLowerCase().includes(lowerQuery) ||
        c.email.toLowerCase().includes(lowerQuery)
      ) {
        mockResults.push({
          type: "customer",
          id: c.id,
          title: c.name,
          subtitle: c.email,
          href: `/customers?id=${c.id}`,
        });
      }
    });

    setResults(mockResults);
  };

  const getIcon = (type: string) => {
    switch (type) {
      case "product":
        return <Package className="h-5 w-5" />;
      case "order":
        return <ShoppingCart className="h-5 w-5" />;
      case "customer":
        return <Users className="h-5 w-5" />;
    }
  };

  const getTypeLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div
      data-testid={testId ?? setSearchViewTestId("container")}
      className="space-y-6"
    >
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {t("views.search.title")}
        </h1>
        <p className="text-muted-foreground mt-2">
          {query ? `Showing results for "${query}"` : "Enter a search query"}
        </p>
      </div>

      <div className="relative max-w-2xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          placeholder={t("components.appHeader.searchPlaceholder")}
          className="h-12 pl-10"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchQuery.trim()) {
              performSearch(searchQuery);
            }
          }}
        />
      </div>

      {results.length === 0 && query ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium">{t("common.noResults")}</p>
            <p className="text-sm text-muted-foreground">
              {t("views.search.tryDifferent")}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {results.map((result) => (
            <Link key={`${result.type}-${result.id}`} to={result.href}>
              <Card className="hover:bg-accent/5 transition-colors cursor-pointer">
                <CardContent className="flex items-center gap-4 p-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent-strong">
                    {getIcon(result.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{result.title}</p>
                      {result.badge && (
                        <Badge variant="outline" className="text-xs">
                          {result.badge}
                        </Badge>
                      )}
                    </div>
                    {result.subtitle && (
                      <p className="text-sm text-muted-foreground truncate">
                        {result.subtitle}
                      </p>
                    )}
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {getTypeLabel(result.type)}
                  </Badge>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
